import React, {ChangeEvent, useCallback, useEffect, useState} from "react";
import {AxiosResponse} from "axios";
import {useTranslation} from 'react-i18next';
import {useUserContext} from "../../../../Contexts/UserContext";
import {getAtriaApi, getBackendApi} from "../../../../Api/Tools/Api";
import {IconTrigger} from "../../../CommonStyle";
import Icon from "../../../../Components/Icon/Icon";
import {Column, ColumnEditorOptions, ColumnEvent} from "primereact/column";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import ModalCreateNewCategories from "../Modals/CreateNewCategoryModal/ModalCreateNewCategories";
import {AddNewCategoryForm, DataTableToolbar, DataTableWithColoredNewRow, EnableDisableLabel} from "./Style";
import {toastError, toastSuccess} from "../../../../Tools/Toast";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {DataTableSelectionMultipleChangeEvent} from "primereact/datatable";
import {InputSwitch} from "primereact/inputswitch";
import {AtriaTitle, InlineRow} from "../../../../Components/Style";
import {LLMFeatureEnum} from "../../../../Models/ApiBackendEnums";
import {Message} from "primereact/message";
import LlmInteractionCategoryDTO = API_ATRIA.Schemas.LlmInteractionCategoryDTO;
import LLMFeatureActivationDTO = API_BACKEND.Schemas.LLMFeatureActivationDTO;

interface LlmInteractionCategory extends LlmInteractionCategoryDTO {
    isNew: boolean;
}

export const LLMInteractionCategoriesEditor: React.FC = () => {
    const [isCategorisationFeatureEnabled, setIsCategorisationFeatureEnabled] = useState<boolean>(true);
    const [isCategorisationEnabled, setIsCategorisationEnabled] = useState<boolean>(false);
    const [categories, setCategories] = useState<LlmInteractionCategory[]>([]);
    const [isModalCreateVisible, setIsModalCreateVisible] = useState<boolean>(false);
    const [selectedCategories, setSelectedCategories] = useState<LlmInteractionCategory[]>([]);

    const [newCategoryName, setNewCategoryName] = useState<string>("");

    const {t} = useTranslation();

    const {botUUID} = useUserContext();

    const MAX_NAME_LENGTH: number = 100;
    const MAX_DESCRIPTION_LENGTH: number = 500;

    const fetchFeatureActivation = useCallback(() => {
        getBackendApi().getFeatureActivationByName(LLMFeatureEnum.CATEGORISE_DIALOG_INTERACTIONS).then((res: AxiosResponse<LLMFeatureActivationDTO>) => {
            setIsCategorisationFeatureEnabled(res?.data?.enabled);
        });
    }, [])

    const fetchIsCategorisationEnabled = useCallback(() => {
        if (!botUUID) {
            return;
        }

        getAtriaApi().isCategorisationEnabled(botUUID)
            .then((res: AxiosResponse<boolean>) => {
                setIsCategorisationEnabled(res.data);
            })
            .catch(err => {
                toastError(t('pages.llm-interaction-categories.messages.errors.categorisation-activation-state'));
                console.error("An error occurred while retrieving enable/disable categorisation state :", err);
            })
    }, [botUUID, t]);

    const fetchCategories = useCallback(() => {
        if (!botUUID) {
            return;
        }

        getAtriaApi().getLlmInteractionCategories(botUUID)
            .then((res: AxiosResponse<LlmInteractionCategoryDTO[]>) => {
                setCategories(res.data.map(item => {
                    return {
                        ...item,
                        isNew: false
                    }
                }));
            })
            .catch(err => {
                toastError(t('pages.llm-interaction-categories.messages.errors.retrieval'));
                console.error("An error occurred while retrieving categories :", err);
            })
    }, [botUUID, t]);

    useEffect(() => {
        fetchFeatureActivation();
        fetchIsCategorisationEnabled();
        fetchCategories();
    }, [fetchFeatureActivation, fetchIsCategorisationEnabled, fetchCategories]);

    const addCategories = useCallback((categoriesToSave: LlmInteractionCategoryDTO[]) => {
        if (categoriesToSave?.length <= 0) {
            return;
        } else if (categoriesToSave?.length === 1) {
            getAtriaApi().createOrUpdateLlmInteractionCategoryForBot(botUUID, categoriesToSave[0]).then((res: AxiosResponse<LlmInteractionCategoryDTO>) => {
                setCategories(prevCategories => [
                    {...res.data, isNew: true},
                    ...prevCategories
                ]);
                setNewCategoryName('');
                toastSuccess(t('pages.llm-interaction-categories.messages.creation-success', {categoryName: categoriesToSave[0].name}));
            }).catch((err) => {
                toastError(t('pages.llm-interaction-categories.messages.errors.creation'))
                console.error("An error occurred while creating category :", err);
            });
        } else {
            getAtriaApi().createLlmInteractionCategoriesForBot(botUUID, categoriesToSave).then((res: AxiosResponse<LlmInteractionCategoryDTO[]>) => {
                if (res.data) {
                    setCategories(prevCategories => [
                        ...res.data.map(item => {
                            return {
                                ...item,
                                isNew: true
                            };
                        }),
                        ...prevCategories
                    ]);
                    setNewCategoryName('');
                    toastSuccess(t("pages.llm-interaction-categories.messages.multiple-creation-success", {categoriesLength: categoriesToSave.length}));
                }
            }).catch((err) => {
                toastError(t('pages.llm-interaction-categories.messages.errors.multiple-creation'))
                console.error("An error occurred while creating categories :", err);
            });
        }
    }, [botUUID, t]);

    const textEditor = useCallback((options: ColumnEditorOptions, maxLength: number) => {
        return <InputText type="text" value={options.value}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              options.editorCallback && options.editorCallback(e.target.value?.substring(0, maxLength));
                          }}/>;
    }, []);

    const renderDeleteSelectionBtn = useCallback(() => {
        const deleteConfirm = (event: { currentTarget: HTMLElement | undefined; }) => {
            confirmPopup({
                target: event.currentTarget,
                message: t('pages.llm-interaction-categories.messages.multiple-deletion-confirm', {selectionCount: selectedCategories?.length}),
                icon: 'pi pi-exclamation-triangle',
                defaultFocus: 'accept',
                acceptLabel: t('general.yes'),
                rejectLabel: t('general.no'),
                accept: () => {
                    getAtriaApi().deleteLlmInteractionCategories(botUUID, selectedCategories.map(item => item.id || 0))
                        .then(() => {
                            const idsToRemove = selectedCategories.map(item => item.id);
                            setCategories(currentCategories => currentCategories.filter(item => !idsToRemove.includes(item.id)));
                            setSelectedCategories([]);
                            toastSuccess(t("pages.llm-interaction-categories.messages.multiple-deletion", {categoriesLength: selectedCategories?.length}));
                        })
                        .catch(err => {
                            toastError(t('pages.llm-interaction-categories.messages.errors.multiple-deletion'))
                            console.error("An error occurred while removing categories :", err);
                        });
                }
            });
        };

        return selectedCategories?.length > 0 ?
            <Button onClick={deleteConfirm}
                    label={t('general.remove-selected-count', {removeCount: selectedCategories?.length})}
                    className='p-button-secondary'
                    icon='icon-trash'
                    severity="danger"
            /> :
            null;
    }, [botUUID, selectedCategories, t]);

    const renderMultipleAddBtn = () => {
        return (
            <Button
                style={{flexGrow: 0, width: '150px'}}
                label={t('general.multiple-add')}
                onClick={() => {
                    setIsModalCreateVisible(true);
                }}
                className='p-button-primary'
                icon='icon-add'
            />
        );
    }

    const renderActionsColumn = useCallback((rowData: LlmInteractionCategory) => {
        const deleteConfirm = (event: { currentTarget: HTMLElement | undefined; }) => {
            confirmPopup({
                target: event.currentTarget,
                message: t('pages.llm-interaction-categories.messages.deletion-confirm'),
                icon: 'pi pi-exclamation-triangle',
                defaultFocus: 'accept',
                acceptLabel: t('general.yes'),
                rejectLabel: t('general.no'),
                accept: () => {
                    if (rowData.id) {
                        getAtriaApi().deleteLlmInteractionCategory(botUUID, rowData.id)
                            .then(() => {
                                setCategories(currentCategories => currentCategories.filter(item => item.id !== rowData.id));
                                setSelectedCategories(currentCategories => currentCategories.filter(item => item.id !== rowData.id));
                                toastSuccess(t("pages.llm-interaction-categories.messages.deletion"));
                            })
                            .catch(err => {
                                toastError(t('pages.llm-interaction-categories.messages.errors.deletion'));
                                console.error("An error occurred while removing category :", err);
                            });
                    }
                }
            });
        };

        return (
            <>
                <IconTrigger onClick={deleteConfirm}>
                    <Icon color='red' size={20} icon='trash'/>
                </IconTrigger>
            </>
        );
    }, [botUUID, t]);

    const handleSubmitAddCategory = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (newCategoryName.trim().length > 0) {
            const newCategory: LlmInteractionCategoryDTO = {
                botUUID: botUUID,
                name: newCategoryName,
            };

            addCategories([newCategory]);
        }
    }, [botUUID, newCategoryName, addCategories]);

    const newItemsClass = useCallback((data: LlmInteractionCategory) => {
        return {
            'coloredRow': data.isNew
        };
    }, []);

    const onCellEditComplete = useCallback((e: ColumnEvent) => {
        let {rowData, newValue, field} = e;

        const currentItem = categories.find(category => category.id === rowData.id);
        if (!currentItem) return;

        const originalValue = currentItem[field];
        if (originalValue?.trim() === newValue?.trim()) {
            return;
        }

        let updatedCategories = [...categories];
        let rowIndex = updatedCategories.findIndex(c => c.id === rowData.id);

        if (rowIndex === -1) return;

        updatedCategories[rowIndex][field] = newValue;

        setCategories(updatedCategories);

        const {isNew, ...item} = currentItem;

        let itemToSave: LlmInteractionCategoryDTO | null = null;

        if (field === 'name') {
            itemToSave = {...item, name: newValue};
        } else if (field === 'description') {
            itemToSave = {...item, description: newValue};
        }

        const updateCategoryInState = (responseCategory: LlmInteractionCategoryDTO, isNewRow: boolean) => {
            setCategories(prevCategories => prevCategories.map(category =>
                category.id === rowData.id
                    ? {...responseCategory, isNew: isNewRow}
                    : category
            ));
        };

        if (itemToSave) {
            getAtriaApi().updateLlmInteractionCategoryForBot(botUUID, itemToSave)
                .then(res => updateCategoryInState(res.data, rowData.isNew))
                .catch(err => {
                    toastError(t('pages.llm-interaction-categories.messages.errors.modification', {field: t('pages.llm-interaction-categories.data.' + field)}));
                    console.error(`An error occurred while updating ${field} data :`, err);
                });
        }
    }, [botUUID, categories, t]);

    return (
        <>
            <AtriaTitle>{t('pages.llm-interaction-categories.title')}</AtriaTitle>
            {isCategorisationFeatureEnabled ? null :
                <Message style={{width: '100%'}} className={'mb-5 w-full justify-content-start'} severity='error'
                         text={t('pages.llm-interaction-categories.messages.categorisation-feature-disabled')}/>}
            <InlineRow className={"mb-5"}>
                <InputSwitch inputId="enable-disable-categorisation" checked={isCategorisationEnabled}
                             onChange={(e) => {
                                 setIsCategorisationEnabled(e.value);
                                 getAtriaApi().enableOrDisableCategorisation(botUUID, e.value)
                                     .then(() => {
                                         if (e.value) {
                                             toastSuccess(t('pages.llm-interaction-categories.messages.categorisation-enabled'));
                                         } else {
                                             toastSuccess(t('pages.llm-interaction-categories.messages.categorisation-disabled'))
                                         }
                                     })
                                     .catch((err) => {
                                         toastError(t('pages.llm-interaction-categories.messages.errors.categorisation-activation'))
                                         console.error('An error occurred while sending enable state to backend : ', err);
                                         setIsCategorisationEnabled(!e.value);
                                     });
                             }}
                />
                <EnableDisableLabel htmlFor="enable-disable-categorisation">
                    {t('pages.llm-interaction-categories.form.enable-disable')}
                </EnableDisableLabel>
            </InlineRow>
            <InlineRow>
                <AddNewCategoryForm className="p-inputgroup flex-1"
                                    onSubmit={handleSubmitAddCategory}>
                    <InputText width={50} height={40} autoFocus
                               type="text"
                               style={{flexGrow: 0}}
                               value={newCategoryName}
                               onChange={(e) => {
                                   setNewCategoryName(e.target.value?.substring(0, MAX_NAME_LENGTH));
                               }}
                    />
                    <Button type="submit"
                            label={t('general.add')}
                            className='p-button-primary'
                            icon='icon-add'
                            disabled={!newCategoryName || newCategoryName.trim().length === 0 || newCategoryName?.length > MAX_NAME_LENGTH}
                    />
                </AddNewCategoryForm>
            </InlineRow>
            <DataTableToolbar>
                {renderDeleteSelectionBtn()}
                {renderMultipleAddBtn()}
            </DataTableToolbar>
            <DataTableWithColoredNewRow
                value={categories}
                rows={20}
                dataKey="id"
                sortField="name"
                sortOrder={1}
                rowClassName={newItemsClass}
                tableStyle={{tableLayout: 'fixed', minWidth: '100%'}}
                editMode="cell"
                emptyMessage={t("pages.llm-interaction-categories.form.empty-data-message")}
                selectionMode={'checkbox'}
                selection={selectedCategories}
                onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<LlmInteractionCategory[]>) => setSelectedCategories(e.value)}
            >
                <Column selectionMode="multiple" headerStyle={{width: '3rem'}}
                        style={{minWidth: '3%', width: '3%', maxWidth: '3%'}}
                />
                <Column field="name" header={t('pages.llm-interaction-categories.data.name')} sortable
                        editor={(options: ColumnEditorOptions) => textEditor(options, MAX_NAME_LENGTH)}
                        onCellEditComplete={onCellEditComplete}
                        style={{minWidth: '20%', width: '20%', maxWidth: '20%'}}
                />
                <Column field="description" header={t('pages.llm-interaction-categories.data.description')}
                        editor={(options: ColumnEditorOptions) => textEditor(options, MAX_DESCRIPTION_LENGTH)}
                        onCellEditComplete={onCellEditComplete}
                        style={{minWidth: '70%', width: '70%', maxWidth: '70%'}}
                />
                <Column field="actions" header={t('general.actions')} body={renderActionsColumn}
                        style={{minWidth: '7%', width: '7%', maxWidth: '7%'}}/>
            </DataTableWithColoredNewRow>
            <ModalCreateNewCategories
                visible={isModalCreateVisible}
                onHide={() => setIsModalCreateVisible(false)}
                addCategories={addCategories}
                botUUID={botUUID}
            />
            <ConfirmPopup/>
        </>
    );
};